import { useEffect, useState } from "react";
import { RadioGroup } from "@headlessui/react";
import { stringFormattingToCurrency, getBadgeText } from "js/formatting";
import { useTranslation } from "react-i18next";
import { isOldAlicUlPolicy } from "js/utilities";
import FormInputError from "components/General/FormInputError";

function OldAlicUlPanel({
  value,
  error,
  isStepTwoChecked,
  handlePremiumChange,
  setStepTwoChecked,
  t,
  setIsDisabled,
}) {
  return (
    <div className="flex items-start gap-2 p-2 relative rounded border border-solid border-ui-gray-medium">
      <div className="flex flex-col items-start relative flex-1 grow">
        <span className="block text-xxs font-bold text-theme-text-quaternary">
          {t("CustomerMakeAPayment-step-two-oldAlicUl-title")}
        </span>
        <div className="mt-3 relative flex flex-col items-start gap-1 w-full">
          <div className="flex items-start gap-1 relative w-full">
            <input
              type="text"
              className="border border-ui-gray-medium h-9 w-full p-2 pl-8 top-1/2 rounded"
              value={value}
              onChange={handlePremiumChange}
              placeholder={t(
                "CustomerMakeAPayment-step-two-oldAlicUl-placeholderText"
              )}
              aria-label="Premium Payment Amount"
            />
            <svg
              className="absolute text-theme-action top-1/2 mt-[-10px] ml-2"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <use href="#ui-icon-dollar"></use>
            </svg>
          </div>
          {error && (
            <FormInputError id="premium-amount-error" errorMessage={error} />
          )}
          <div className="text-xxs font-normal text-theme-text-secondary">
            {t("CustomerMakeAPayment-step-two-oldAlicUl-helpText")}
          </div>
        </div>
      </div>
      <label className="ui-checkbox">
        <input
          type="checkbox"
          checked={isStepTwoChecked}
          onChange={(e) => {
            setStepTwoChecked(e.target.checked);
            e.target.checked ? setIsDisabled(false) : setIsDisabled(true);
          }}
        />
        <div className="ui-checkmark">
          <svg
            className="ui-icon w-2 h-2 text-white"
            focusable="false"
            aria-hidden="true"
            role="presentation"
          >
            <use href="#ui-icon-checkmark"></use>
          </svg>
        </div>
      </label>
    </div>
  );
}

const PanelTwo = ({
  setCurrentStep,
  selectedPolicy,
  loan,
  setPaymentType,
  setIsDisabled,
  setAmount,
  isStepTwoChecked,
  setStepTwoChecked,
}) => {
  const { t } = useTranslation();
  const [buttonOneChecked, setButtonOneChecked] = useState(true);
  const [buttonTwoChecked, setButtonTwoChecked] = useState(false);
  const [sectionTwo, setSectionTwo] = useState("");
  const [value, setValue] = useState("");
  const [error, setError] = useState();

  useEffect(() => {
    if (!loan) {
      setButtonOneChecked(true);
      setPaymentType("premiumpayment");
      setCurrentStep(3);
    }
  }, [loan]);

  useEffect(() => {
    if (isOldAlicUlPolicy(selectedPolicy)) {
      const defaultVal = selectedPolicy?.modalPremiumAmountTotal
        ? String(Number(selectedPolicy?.modalPremiumAmountTotal).toFixed(2))
        : "";
      setValue(defaultVal);
      setAmount(defaultVal);
      //setIsDisabled(false);
    }
  }, [selectedPolicy]);

  const handlePremiumChange = (e) => {
    const val = e.target.value.replace(/[^0-9.]/g, "");
    setValue(val);
    const num = parseFloat(val);
    if (!val || num <= 0) {
      setError("Please enter an amount greater than $0.00");
      setIsDisabled(true);
      setAmount(undefined);
    } else {
      setError(undefined);
      setIsDisabled(false);
      setAmount(val);
      setStepTwoChecked(true);
    }
  };

  if (isOldAlicUlPolicy(selectedPolicy)) {
    return (
      <OldAlicUlPanel
        value={value}
        error={error}
        isStepTwoChecked={isStepTwoChecked}
        handlePremiumChange={handlePremiumChange}
        setStepTwoChecked={setStepTwoChecked}
        t={t}
        setIsDisabled={setIsDisabled}
      />
    );
  }

  return (
    <>
      {selectedPolicy && (
        <div className="px-0 md:px-16">
          <form>
            <RadioGroup
              className="ui-form-card-row cursor-pointer font-semibold"
              name="panelTwoSelection"
              value={sectionTwo}
              onChange={setSectionTwo}
            >
              <RadioGroup.Option
                id="ID_premiumPaymentType"
                className={({ checked }) =>
                  `${
                    !loan || (checked && buttonOneChecked)
                      ? "ui-form-card selected"
                      : "ui-form-card"
                  }`
                }
                tabIndex="0"
                aria-label="Select premium payment"
                aria-controls="ID_paymentTypeStep1Option"
                aria-expanded="false"
                value="premiumpayment"
                onClick={() => {
                  setButtonTwoChecked(false);
                  setButtonOneChecked(true);
                  setPaymentType("premiumpayment");
                  setCurrentStep(3);
                  setIsDisabled(true);
                }}
              >
                {({ checked }) => (
                  <>
                    <RadioGroup.Label
                      className="block text-xxs text-ui-gray-default pr-8 cursor-pointer font-semibold"
                      as="div"
                    >
                      {`Contract # ${selectedPolicy.oldPolicy}`}
                    </RadioGroup.Label>
                    <RadioGroup.Label className="block text-black">
                      {t("CustomerMakeAPayment-step-two-title1")}
                    </RadioGroup.Label>
                    <RadioGroup.Label className="block text-xxs text-ui-gray-default pr-8 cursor-pointer font-semibold">
                      <div className="flex justify">
                        <svg
                          className={`ui-icon w-3 h-3 mr-2 self-center text-ui-${
                            getBadgeText(selectedPolicy).includes("Past due")
                              ? "red"
                              : "green"
                          }`}
                          focusable="false"
                          aria-hidden="true"
                          role="presentation"
                        >
                          <use href="#ui-icon-circle-solid-exclamation"></use>
                        </svg>
                        <p className="self-center font-semibold">
                          {getBadgeText(selectedPolicy).includes("Past due")
                            ? t("CustomerMakeAPayment-step-two-past-due")
                            : t("CustomerMakeAPayment-step-two-paid-to-date")}
                          {stringFormattingToCurrency(
                            selectedPolicy?.modalPremiumAmountTotal
                          )}
                        </p>
                      </div>
                    </RadioGroup.Label>
                    <div className="text-xxs font-semibold pr-8 group-hover:underline">
                      <div className="ui-form-card-checkbox">
                        <svg
                          className="ui-icon w-2 h-2 text-white hidden"
                          focusable="false"
                          aria-hidden="true"
                          role="presentation"
                        >
                          <use href="#ui-icon-checkmark"></use>
                        </svg>
                      </div>
                    </div>
                  </>
                )}
              </RadioGroup.Option>
              {loan && (
                <RadioGroup.Option
                  id="ID_paymentStep1Option1"
                  className={({ checked }) =>
                    `${
                      checked && buttonTwoChecked
                        ? "ui-form-card selected"
                        : "ui-form-card"
                    }`
                  }
                  tabIndex="0"
                  aria-label="Select loan payment type"
                  aria-controls="ID_paymentTypeStep2Options"
                  aria-expanded="false"
                  value="loanpayment"
                  onClick={() => {
                    setButtonTwoChecked(true);
                    setButtonOneChecked(false);
                    setPaymentType("loan");
                    setCurrentStep(3);
                    setIsDisabled(true);
                  }}
                >
                  {({ checked }) => (
                    <>
                      <RadioGroup.Label
                        className="block text-xxs text-ui-gray-default pr-8 cursor-pointer font-semibold"
                        as="div"
                      >
                        {`${t("CustomerMakeAPayment-step-two-policy-number")}${
                          selectedPolicy.oldPolicy
                        }`}
                      </RadioGroup.Label>
                      <RadioGroup.Label className="block text-black">
                        {t("CustomerMakeAPayment-step-two-loan-title1")}
                      </RadioGroup.Label>
                      <RadioGroup.Label className="text-xxs"></RadioGroup.Label>
                      <RadioGroup.Label className="block text-xxs text-ui-gray-default pr-8 cursor-pointer font-semibold">
                        <div className="flex justify">
                          <svg
                            className={`ui-icon w-3 h-3 mr-2 self-center text-ui-green`}
                            focusable="false"
                            aria-hidden="true"
                            role="presentation"
                          >
                            <use href="#ui-icon-circle-solid-exclamation"></use>
                          </svg>
                          <p className="self-center font-semibold">
                            {t("CustomerMakeAPayment-step-two-loan-title2")}
                          </p>
                        </div>
                      </RadioGroup.Label>
                      <div className="text-xxs font-semibold pr-8 group-hover:underline">
                        <div className="ui-form-card-checkbox">
                          <svg
                            className="ui-icon w-2 h-2 text-white hidden"
                            focusable="false"
                            aria-hidden="true"
                            role="presentation"
                          >
                            <use href="#ui-icon-checkmark"></use>
                          </svg>
                        </div>
                      </div>
                    </>
                  )}
                </RadioGroup.Option>
              )}
            </RadioGroup>
          </form>
        </div>
      )}
    </>
  );
};

export default PanelTwo;
